import { PublicDiscoveryViewSerializer } from "@app-contexts/public/features/discovery/data/types/discovery";
import {
  CityLandingPageEventSerializer,
  PublicBaseEventSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { getEventId } from "@shared/modules/events/utils/event-utils";
import { useMemo } from "react";
import useUserBasedFavoriteEventService from "../../data/services/useUserBasedFavoriteEventService";

export function useFavoriteHeartButton(favoriteData?: {
  event?:
    | PublicBaseEventSerializer
    | PublicDiscoveryViewSerializer
    | CityLandingPageEventSerializer;
  displayRight?: boolean;
}) {
  const { isLoggedIn, userProfileData: user } = useUserSessionAuthState();

  const favoriteEventParams = {
    ordering: "event_starts_on,id",
    page_size: 100,
  };

  const { usePaginated, useCreate, useDelete } =
    useUserBasedFavoriteEventService();
  const { data: favoriteEvents, isFetching: isFetchingFavs } = usePaginated({
    params: favoriteEventParams,
    config: {
      showToastOnError: false, // prevents multiple error toasts from being spammed
    },
    options: {
      enabled: Boolean(isLoggedIn && favoriteData), // only enable favorites when logged in AND when event data is passed in
    },
  });

  const addFavoriteEventMutation = useCreate();
  const removeFavoriteEventMutation = useDelete();

  const isLoading =
    isFetchingFavs ||
    addFavoriteEventMutation.isLoading ||
    removeFavoriteEventMutation.isLoading;

  const displayRight = Boolean(favoriteData?.displayRight);

  const favoriteEventIds = useMemo(() => {
    return (
      favoriteEvents?.pages
        ?.map((page) => page?.results?.map((res) => res?.event?.id))
        .flat() ?? []
    );
  }, [favoriteEvents]);

  const extractedEventId = useMemo(() => {
    if (favoriteData && favoriteData.event) {
      return getEventId(favoriteData.event);
    }
    return undefined;
  }, [favoriteData]);

  return {
    user,
    displayRight,
    isLoading,
    isLoggedIn,
    extractedEventId,
    favoriteEventIds,
    favoriteEvents,
    addFavoriteEventMutation,
    removeFavoriteEventMutation,
  };
}
